import { Box, Stack } from 'braid-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import SkeletonLine from 'src/components/SkeletonLine/SkeletonLine';
import useAuthenticated from 'src/hooks/useAuthenticated';
import { sendHotJarEvent } from 'src/hooks/useHotjarEvent';

import { CareerFeedAIAssistantWidget } from './CareerFeedAIAssistantContainer';
import { CareerFeedAIAssistantContent } from './CareerFeedAIAssistantContent/CareerFeedAIAssistantContent';
import { useCareerFeedAIAssistant } from './hooks/useCareerFeedAIAssistant';
import { useRecommendationForCareerFeedAiAssistant } from './hooks/useRecommendationForCareerFeedAiAssistant';

/** *
 * This component is responsible for displaying the AI Assistant for the Career Feed.
 * This is still in in discovery phase and the implementation of this component might change drastically.
 * The reason why this is in the code base is so that people can start playing around with it
 * and get feedback on the implementation.
 */
export const CareerFeedAIAssistant = () => {
  const {
    error: isRecommendationError,
    loading: isRecommendationLoading,
    isRefreshLoading,
    jobIds,
  } = useRecommendationForCareerFeedAiAssistant();

  const authenticateState = useAuthenticated();

  const {
    /* sequence, */
    persistedCandidateChatContext,
    progress,
    tracking: {
      /* trackGenAIChatOutputDisplayed,
      trackGenAIChatStarted, */
      trackGenAIChatCtaDisplayed,
    },
  } = useCareerFeedAIAssistant();

  const [isCtaDisplayedEventFired, setIsCtaDisplayedEventFired] =
    useState(false);

  const [isInView, setIsInView] = useState(false);

  const { ref: trackedComponent } = useInView({
    onChange(inView) {
      setIsInView(inView);

      /* if (progress !== 'recsRefined-withPersistedData') {
        trackGenAIChatOutputDisplayed();
      }

      if (sequence === 1) {
        trackGenAIChatStarted();
      } */
    },
    delay: 500,
    threshold: 0.5,
  });

  const readyToFireTracking = useMemo(
    () => isInView && authenticateState === 'authenticated',
    [authenticateState, isInView],
  );

  useEffect(() => {
    if (
      readyToFireTracking &&
      progress === 'initial' &&
      !isCtaDisplayedEventFired
    ) {
      trackGenAIChatCtaDisplayed();
      sendHotJarEvent('homepage_genAI_start');
      setIsCtaDisplayedEventFired(true);
    }
  }, [
    isCtaDisplayedEventFired,
    progress,
    readyToFireTracking,
    trackGenAIChatCtaDisplayed,
  ]);

  const jobsCount = jobIds.length;
  const isLoadingState =
    (isRecommendationLoading && !isRefreshLoading) ||
    (progress === 'initial' && isRefreshLoading);

  if (isRecommendationError) {
    return null;
  }

  if (!persistedCandidateChatContext && !isLoadingState && jobsCount === 0) {
    return null;
  }

  return (
    <Box ref={trackedComponent}>
      <CareerFeedAIAssistantWidget showHeading={!isLoadingState}>
        {isLoadingState ? (
          <Stack space="medium">
            <SkeletonLine type="text" width={200} size="small" />
            <SkeletonLine type="text" width={260} size="xsmall" />
          </Stack>
        ) : (
          <CareerFeedAIAssistantContent />
        )}
      </CareerFeedAIAssistantWidget>
    </Box>
  );
};
